import { getUsStateOrTerritoryLabel } from '../location';

export type UsSupportRoutingMode =
  | 'federal'
  | 'state_routed'
  | 'county_or_city_routed'
  | 'school_district_routed'
  | 'tribal_or_territory_routed'
  | 'local_directory';

export type UsSupportRouteKey =
  | 'benefits_gov_finder'
  | 'snap_state_directory'
  | 'wic_contacts'
  | 'tanf_start'
  | 'medicaid_state_overviews'
  | 'healthcare_gov_medicaid_chip'
  | 'child_support_contacts'
  | 'irs_child_tax_credit'
  | 'irs_eitc'
  | 'irs_child_and_dependent_care_credit'
  | 'irs_free_tax_help'
  | 'childcare_financial_assistance'
  | 'head_start_locator'
  | 'school_meals_for_families'
  | 'liheap_start'
  | 'ssa_child_ssi'
  | 'ssa_survivors'
  | 'emergency_food_help'
  | 'tenant_rights'
  | 'avoid_eviction'
  | 'emergency_rent_help'
  | 'rental_assistance'
  | 'section_8'
  | 'hud_pha_contacts'
  | 'housing_discrimination'
  | 'domestic_violence_hotline'
  | 'emergency_housing'
  | 'hud_housing_counselors'
  | 'womenslaw_custody_basics'
  | 'womenslaw_general_custody'
  | 'lawhelp_org'
  | 'womenslaw_restraining_orders'
  | 'womenslaw_home'
  | 'lsc_legal_help'
  | 'travel_state_minors'
  | 'nami_support_groups'
  | 'help_211'
  | 'find_treatment'
  | 'lifeline_988'
  | 'samhsa_national_helpline'
  | 'hrsa_health_centers'
  | 'nces_district_search'
  | 'nche_mckinney_vento'
  | 'school_meals_family_portal'
  | 'community_eligibility_provision'
  | 'childcare_state_resources'
  | 'childcare_report_concern'
  | 'idea_parents_families'
  | 'idea_initial_evaluations'
  | 'section_504'
  | 'idea_part_c'
  | 'parent_center_hub'
  | 'ed_ocr_complaint'
  | 'cdc_adhd'
  | 'cdc_autism'
  | 'idea_home'
  | 'nimh_child_mental_health'
  | 'dol_fmla'
  | 'state_labor_offices'
  | 'dol_sick_leave'
  | 'dol_maternal_health'
  | 'eeoc_pregnancy_discrimination'
  | 'careeronestop_unemployment'
  | 'careeronestop_local_help'
  | 'irs_self_employed_tax_center'
  | 'irs_estimated_taxes'
  | 'irs_sole_proprietors';

export type UsSupportRoute = {
  label: string;
  url: string;
};

export type UsSupportRoutingCard = {
  link_label?: string | null;
  link_url?: string | null;
  routing_mode?: UsSupportRoutingMode | null;
  rules_vary_by_state?: boolean | null;
};

export type UsSupportRoutingContext = {
  countryCode?: string | null;
  postcode?: string | null;
  support_us_state?: string | null;
  support_us_county_or_city?: string | null;
  support_us_school_district?: string | null;
  support_us_school_zip?: string | null;
  support_us_tribal_or_territory?: string | null;
};

export type UsSupportRoutingSummary = {
  title: string;
  body: string;
  actionLabel: string;
  actionUrl: string | null;
  missingFieldLabels: string[];
};

export const US_SUPPORT_ROUTE_MAP: Record<UsSupportRouteKey, UsSupportRoute> = {
  benefits_gov_finder: {
    label: 'Benefits.gov benefit finder',
    url: 'https://www.benefits.gov/benefit-finder',
  },
  snap_state_directory: {
    label: 'USDA SNAP state directory',
    url: 'https://www.fns.usda.gov/snap/state-directory',
  },
  wic_contacts: {
    label: 'USDA WIC program contacts',
    url: 'https://www.fns.usda.gov/wic/how-to-apply',
  },
  tanf_start: {
    label: 'USAGov TANF',
    url: 'https://www.usa.gov/welfare-benefits',
  },
  medicaid_state_overviews: {
    label: 'Medicaid state overviews',
    url: 'https://www.medicaid.gov/about-us/where-can-people-get-help-medicaid-chip/index.html',
  },
  healthcare_gov_medicaid_chip: {
    label: 'HealthCare.gov Medicaid/CHIP',
    url: 'https://www.healthcare.gov/medicaid-chip/',
  },
  child_support_contacts: {
    label: 'State and tribal child support contacts',
    url: 'https://www.acf.hhs.gov/css/office-child-support-enforcement-child-support-program-contacts',
  },
  irs_child_tax_credit: {
    label: 'IRS Child Tax Credit',
    url: 'https://www.irs.gov/credits-deductions/individuals/child-tax-credit',
  },
  irs_eitc: {
    label: 'IRS EITC',
    url: 'https://www.irs.gov/credits-deductions/individuals/earned-income-tax-credit',
  },
  irs_child_and_dependent_care_credit: {
    label: 'IRS child and dependent care credit',
    url: 'https://www.irs.gov/credits-deductions/individuals/child-and-dependent-care-credit-information',
  },
  irs_free_tax_help: {
    label: 'IRS free tax help',
    url: 'https://www.irs.gov/individuals/free-tax-return-preparation-for-qualifying-taxpayers',
  },
  childcare_financial_assistance: {
    label: 'ChildCare.gov financial assistance',
    url: 'https://childcare.gov/consumer-education/get-help-paying-for-child-care',
  },
  head_start_locator: {
    label: 'Head Start center locator',
    url: 'https://eclkc.ohs.acf.hhs.gov/center-locator',
  },
  school_meals_for_families: {
    label: 'USDA school meals for families',
    url: 'https://www.fns.usda.gov/school-meals/applying-free-and-reduced-price-school-meals',
  },
  liheap_start: {
    label: 'Benefits.gov energy help',
    url: 'https://www.acf.hhs.gov/ocs/low-income-home-energy-assistance-program-liheap',
  },
  ssa_child_ssi: {
    label: 'SSA SSI for children',
    url: 'https://www.ssa.gov/benefits/disability/apply-child.html',
  },
  ssa_survivors: {
    label: 'SSA survivors',
    url: 'https://www.ssa.gov/survivor',
  },
  emergency_food_help: {
    label: 'USAGov emergency food help',
    url: 'https://www.usa.gov/food-help',
  },
  tenant_rights: {
    label: 'USAGov tenant rights',
    url: 'https://www.usa.gov/housing-help',
  },
  avoid_eviction: {
    label: 'USAGov avoid eviction',
    url: 'https://www.usa.gov/eviction',
  },
  emergency_rent_help: {
    label: 'USAGov emergency rent assistance',
    url: 'https://www.usa.gov/emergency-rental-assistance',
  },
  rental_assistance: {
    label: 'USAGov rental assistance',
    url: 'https://www.usa.gov/rental-housing-programs',
  },
  section_8: {
    label: 'USAGov Section 8',
    url: 'https://www.usa.gov/housing-choice-voucher-program',
  },
  hud_pha_contacts: {
    label: 'HUD PHA contact info',
    url: 'https://www.hud.gov/program_offices/public_indian_housing/pha/contacts',
  },
  housing_discrimination: {
    label: 'USAGov housing discrimination',
    url: 'https://www.usa.gov/housing-discrimination',
  },
  domestic_violence_hotline: {
    label: 'National Domestic Violence Hotline',
    url: 'https://www.thehotline.org/',
  },
  emergency_housing: {
    label: 'USAGov emergency housing',
    url: 'https://www.usa.gov/emergency-housing-help',
  },
  hud_housing_counselors: {
    label: 'HUD housing counselors',
    url: 'https://www.hud.gov/findacounselor',
  },
  womenslaw_custody_basics: {
    label: 'WomensLaw custody basics',
    url: 'https://www.womenslaw.org/laws/general/custody',
  },
  womenslaw_general_custody: {
    label: 'WomensLaw general custody',
    url: 'https://www.womenslaw.org/laws/general/custody/all',
  },
  lawhelp_org: {
    label: 'LawHelp.org',
    url: 'https://www.lawhelp.org/',
  },
  womenslaw_restraining_orders: {
    label: 'WomensLaw restraining orders',
    url: 'https://www.womenslaw.org/laws/general/restraining-orders',
  },
  womenslaw_home: {
    label: 'WomensLaw',
    url: 'https://www.womenslaw.org/',
  },
  lsc_legal_help: {
    label: 'LSC legal help',
    url: 'https://www.lsc.gov/about-lsc/what-legal-aid/i-need-legal-help',
  },
  travel_state_minors: {
    label: 'Travel.State.gov minors',
    url: 'https://travel.state.gov/content/travel/en/passports/need-passport/under-16.html',
  },
  nami_support_groups: {
    label: 'NAMI local support',
    url: 'https://www.nami.org/support-education/support-groups/',
  },
  help_211: {
    label: '211 local help',
    url: 'https://www.211.org/',
  },
  find_treatment: {
    label: 'FindTreatment.gov',
    url: 'https://findtreatment.gov/',
  },
  lifeline_988: {
    label: '988 Lifeline',
    url: 'https://988lifeline.org/',
  },
  samhsa_national_helpline: {
    label: 'SAMHSA treatment help',
    url: 'https://www.samhsa.gov/find-help/national-helpline',
  },
  hrsa_health_centers: {
    label: 'HRSA health centers',
    url: 'https://findahealthcenter.hrsa.gov/',
  },
  nces_district_search: {
    label: 'NCES district search',
    url: 'https://nces.ed.gov/ccd/districtsearch/',
  },
  nche_mckinney_vento: {
    label: 'NCHE McKinney-Vento',
    url: 'https://nche.ed.gov/mckinney-vento/',
  },
  school_meals_family_portal: {
    label: 'USDA school meals for families',
    url: 'https://www.fns.usda.gov/school-meals/applying-free-and-reduced-price-school-meals',
  },
  community_eligibility_provision: {
    label: 'USDA CEP',
    url: 'https://www.fns.usda.gov/cn/community-eligibility-provision',
  },
  childcare_state_resources: {
    label: 'ChildCare.gov state resources',
    url: 'https://childcare.gov/state-resources-home',
  },
  childcare_report_concern: {
    label: 'ChildCare.gov report a concern',
    url: 'https://childcare.gov/consumer-education/child-care-complaints',
  },
  idea_parents_families: {
    label: 'IDEA parents and families',
    url: 'https://sites.ed.gov/idea/parents-families/',
  },
  idea_initial_evaluations: {
    label: 'IDEA initial evaluations',
    url: 'https://sites.ed.gov/idea/regs/b/d/300.301',
  },
  section_504: {
    label: 'Section 504',
    url: 'https://www.ed.gov/laws-and-policy/civil-rights-laws/disability-discrimination/section-504-of-the-rehabilitation-act-of-1973',
  },
  idea_part_c: {
    label: 'IDEA Part C',
    url: 'https://sites.ed.gov/idea/idea-files/part-c-early-intervention-for-infants-and-toddlers-with-disabilities/',
  },
  parent_center_hub: {
    label: 'Find your Parent Center',
    url: 'https://www.parentcenterhub.org/find-your-center/',
  },
  ed_ocr_complaint: {
    label: 'ED file a complaint',
    url: 'https://www2.ed.gov/about/offices/list/ocr/complaintintro.html',
  },
  cdc_adhd: {
    label: 'CDC diagnosing ADHD',
    url: 'https://www.cdc.gov/adhd/diagnosis/index.html',
  },
  cdc_autism: {
    label: 'CDC autism diagnosis',
    url: 'https://www.cdc.gov/autism/diagnosis/index.html',
  },
  idea_home: {
    label: 'IDEA website',
    url: 'https://sites.ed.gov/idea/',
  },
  nimh_child_mental_health: {
    label: 'NIMH child and adolescent mental health',
    url: 'https://www.nimh.nih.gov/health/topics/child-and-adolescent-mental-health',
  },
  dol_fmla: {
    label: 'DOL FMLA',
    url: 'https://www.dol.gov/agencies/whd/fmla',
  },
  state_labor_offices: {
    label: 'State labor offices',
    url: 'https://www.dol.gov/agencies/whd/state/contacts',
  },
  dol_sick_leave: {
    label: 'DOL sick leave',
    url: 'https://www.dol.gov/general/topic/workhours/sickleave',
  },
  dol_maternal_health: {
    label: 'DOL maternal health',
    url: 'https://www.dol.gov/agencies/wb/maternal-health',
  },
  eeoc_pregnancy_discrimination: {
    label: 'EEOC pregnancy discrimination',
    url: 'https://www.eeoc.gov/pregnancy-discrimination',
  },
  careeronestop_unemployment: {
    label: 'CareerOneStop unemployment finder',
    url: 'https://www.careeronestop.org/LocalHelp/UnemploymentBenefits/find-unemployment-benefits.aspx',
  },
  careeronestop_local_help: {
    label: 'CareerOneStop local help',
    url: 'https://www.careeronestop.org/LocalHelp/local-help.aspx',
  },
  irs_self_employed_tax_center: {
    label: 'IRS self-employed tax center',
    url: 'https://www.irs.gov/businesses/small-businesses-self-employed/self-employed-individuals-tax-center',
  },
  irs_estimated_taxes: {
    label: 'IRS estimated taxes',
    url: 'https://www.irs.gov/businesses/small-businesses-self-employed/estimated-taxes',
  },
  irs_sole_proprietors: {
    label: 'IRS sole proprietors',
    url: 'https://www.irs.gov/businesses/small-businesses-self-employed/sole-proprietorships',
  },
};

const getLocalAreaLabel = (context: UsSupportRoutingContext) =>
  context.support_us_county_or_city || context.postcode || null;

const getSchoolRoutingLabel = (context: UsSupportRoutingContext) =>
  context.support_us_school_district || context.support_us_school_zip || context.postcode || null;

const getSettingsFieldLabels = (
  mode: UsSupportRoutingMode,
  context: UsSupportRoutingContext,
) => {
  switch (mode) {
    case 'state_routed':
      return context.support_us_state ? [] : ['state / territory'];
    case 'county_or_city_routed':
      return getLocalAreaLabel(context) ? [] : ['county / city or ZIP code'];
    case 'school_district_routed':
      return getSchoolRoutingLabel(context) ? [] : ['school district or school ZIP'];
    case 'tribal_or_territory_routed':
      return context.support_us_tribal_or_territory || context.support_us_state
        ? []
        : ['state / territory or tribal pathway'];
    case 'local_directory':
      return getLocalAreaLabel(context) ? [] : ['ZIP code or county / city'];
    case 'federal':
    default:
      return [];
  }
};

export const getUsSupportRoute = (key: UsSupportRouteKey) =>
  US_SUPPORT_ROUTE_MAP[key];

export const buildUsSupportRoutingSummary = (
  card: UsSupportRoutingCard,
  context: UsSupportRoutingContext,
): UsSupportRoutingSummary | null => {
  if (context.countryCode !== 'US' || !card.routing_mode || card.routing_mode === 'federal') {
    return null;
  }

  const stateLabel = getUsStateOrTerritoryLabel(context.support_us_state);
  const localLabel = getLocalAreaLabel(context);
  const schoolLabel = getSchoolRoutingLabel(context);
  const tribalLabel = context.support_us_tribal_or_territory || stateLabel || null;
  const actionLabel =
    card.link_label?.trim() ||
    (card.routing_mode === 'local_directory'
      ? 'Open the live local directory'
      : card.routing_mode === 'school_district_routed'
        ? 'Open the district or school starting page'
        : card.routing_mode === 'tribal_or_territory_routed'
          ? 'Open the state, tribal, or territory contacts page'
          : 'Open the official state handoff page');

  switch (card.routing_mode) {
    case 'state_routed':
      return {
        title: card.rules_vary_by_state ? 'Rules vary by state' : 'Use your state route next',
        body: stateLabel
          ? `Kindred has your saved state / territory as ${stateLabel}. Start with the national explainer, then use the official state handoff page for the next step.`
          : 'This topic changes by state. Save your state / territory in Settings so Kindred can point you to the right official next source before you keep going.',
        actionLabel,
        actionUrl: card.link_url ?? null,
        missingFieldLabels: getSettingsFieldLabels(card.routing_mode, context),
      };
    case 'county_or_city_routed':
      return {
        title: 'Use your local area next',
        body: localLabel
          ? `Start with the national explainer above, then use ${localLabel} as the local handoff for this topic. Local rent help, shelters, code enforcement, and hardship routes often change by county, city, or ZIP.`
          : 'Start with the national explainer above, then use your local route. Add your county / city or keep your ZIP code current in Settings for a cleaner handoff.',
        actionLabel,
        actionUrl: card.link_url ?? null,
        missingFieldLabels: getSettingsFieldLabels(card.routing_mode, context),
      };
    case 'school_district_routed':
      return {
        title: 'Use your school route next',
        body: schoolLabel
          ? `Start with the national explainer above, then use ${schoolLabel} as the school handoff for this topic. Enrollment, meals, transport, and disability support often break at district or school level.`
          : 'Start with the national explainer above, then use the school route. Add a school district or school ZIP in Settings so Kindred can point you to the right next source.',
        actionLabel,
        actionUrl: card.link_url ?? null,
        missingFieldLabels: getSettingsFieldLabels(card.routing_mode, context),
      };
    case 'tribal_or_territory_routed':
      return {
        title: 'Use the right tribal or territory path when it applies',
        body: tribalLabel
          ? `Start with the national explainer above, then use ${tribalLabel} as the next routing cue here. Some U.S. programs run through tribal or territory systems instead of the usual state path.`
          : 'Start with the national explainer above, then use the right tribal or territory route where it applies. Add that in Settings if you need it.',
        actionLabel,
        actionUrl: card.link_url ?? null,
        missingFieldLabels: getSettingsFieldLabels(card.routing_mode, context),
      };
    case 'local_directory':
      return {
        title: 'Use a live local directory next',
        body: localLabel
          ? `Start with the national explainer above, then use ${localLabel} to narrow the local help you need. This is safer than hard-coding one office, threshold, or wait time here.`
          : 'Start with the national explainer above, then use a live local directory. Keep your ZIP code current, and add county / city if you have it, for a cleaner local handoff.',
        actionLabel,
        actionUrl: card.link_url ?? null,
        missingFieldLabels: getSettingsFieldLabels(card.routing_mode, context),
      };
    default:
      return null;
  }
};
